import { Box, Typography } from '@mui/material';
import { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '../hooks/useAnalytics';
import { StyledButton } from './ui';

/**
 * Loose email shape check. The input is `type="email"`, but the form is
 * submitted with `noValidate` so the browser bubble never fires and the error
 * renders inline instead.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Subscribe card for the super footer.
 *
 * Captures an email address and hands it to the full ActiveCampaign subscribe
 * form on `/subscribe`. The AC list (form 21) also requires first name, last
 * name, company and a reCAPTCHA, so the footer deliberately does not post to
 * ActiveCampaign itself - it collects the address, then pre-fills it on the
 * real form so the visitor only completes what is left.
 *
 * The address travels in router state rather than a query string to keep it out
 * of the URL, browser history and analytics page paths.
 *
 * @returns {JSX.Element} Footer subscribe card
 */
function FooterSubscribe() {
  const navigate = useNavigate();
  const { trackCTA } = useAnalytics();
  const fieldId = useId();
  const errorId = `${fieldId}-error`;
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  /** @param {import('react').ChangeEvent<HTMLInputElement>} event - Input change event */
  const handleChange = (event) => {
    setEmail(event.target.value);
    if (error) {
      setError('');
    }
  };

  /** @param {import('react').FormEvent<HTMLFormElement>} event - Form submit event */
  const handleSubmit = (event) => {
    event.preventDefault();
    const value = email.trim();

    if (!EMAIL_PATTERN.test(value)) {
      setError(
        value ? 'Enter a valid email address.' : 'Enter your email address.'
      );
      return;
    }

    setError('');
    trackCTA('footer_subscribe', 'Subscribe', {
      destination: '/subscribe',
      section: 'subscribe_card',
    });
    navigate('/subscribe', { state: { email: value } });
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{
        mt: 4,
        p: 2.5,
        maxWidth: '360px',
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '0 12px 0 12px',
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          mb: 1,
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: 'var(--color-primary)',
        }}
      >
        Subscribe
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: 'rgba(255, 255, 255, 0.7)',
          fontSize: '0.85rem',
          lineHeight: 1.6,
          mb: 2,
        }}
      >
        Updates on offerings, content and events.
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Box
          component="input"
          type="email"
          id={fieldId}
          name="email"
          value={email}
          onChange={handleChange}
          placeholder="Enter your email"
          autoComplete="email"
          aria-label="Email address"
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          sx={{
            flex: '1 1 170px',
            minWidth: 0,
            px: 1.5,
            py: 1,
            fontFamily: 'inherit',
            fontSize: '0.9rem',
            color: '#ffffff',
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '8px',
            outline: 'none',
            transition: 'border-color 0.2s, background-color 0.2s',
            '&::placeholder': {
              color: 'rgba(255, 255, 255, 0.45)',
            },
            '&:focus': {
              borderColor: 'var(--color-primary)',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        />

        <StyledButton
          type="submit"
          size="small"
          sx={{ flex: '0 0 auto', py: 1, color: 'var(--color-primary-text)' }}
        >
          Subscribe
        </StyledButton>
      </Box>

      {error && (
        <Typography
          id={errorId}
          role="alert"
          variant="body2"
          sx={{ mt: 1, fontSize: '0.8rem', color: '#ffb4ab' }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
}

export default FooterSubscribe;
